/**
 * The processor's refly flags (MB's R127, 27 Oct 2026; spec docs/specs/project-reporting/refly-list.md §10).
 *
 * In Line review (and under a line's plots in Flight data QC) the processor flags the bad section of a flown line by
 * its zFids: typed, picked from a QC exceedance, or dragged on the plots. The flag is kept (`refly_flag`, synced) with
 * the zFids exactly as given; the line is then reviewed with MB's patch over its flags (Suggested patches, decisions
 * 155 and 194), and the Refly list gives the line a row per flag with that zFid range (decision 543, 3B).
 *
 * A zFid is placed on the line by the line status's newest Flight data QC result: its first and last zFid at the
 * logged start and end of the run (`qcFidRun`), by distance in between (`alongOfFid`). zFids are written without
 * thousands separators (R129, `formatZfid`).
 */
import type { QcEvent, QcLineResult } from './geosoft-qc.js';
import { alongOfFid } from './lns-files.js';
import { parseLineNumber } from './lines.js';
import type { Common, LineStatus, PlannedLine, Uuid } from './model.js';
import { qcFidRun, suggestPatch, type BadSection, type PatchSuggestion } from './patch-suggestions.js';
import { plannedLineRole } from './reflights.js';
import { formatZfid, zfidRangeText } from './zfid.js';

/** Line review's reason limit (and the flag's). */
export const FLAG_REASON_MAX = 100;

/** `refly_flag` (SQLite 0051, Postgres 20261027100000; synced): one section of a flown line the processor flagged. */
export interface ReflyFlag extends Common {
  readonly project_id: Uuid;
  readonly line_status_id: Uuid;
  /**
   * The zFids as given (whole numbers) at `from_m` and `to_m`: on a line flown against its planned direction
   * `zfid_start` is the larger.
   */
  readonly zfid_start: number;
  readonly zfid_end: number;
  /** Where they lie, in metres along the original line from its first point (`from_m < to_m`). */
  readonly from_m: number;
  readonly to_m: number;
  /** The Flight data QC test the section was picked from (`terrain_clearance`, ...), or ''. */
  readonly test_key: string;
  /** The processor's words (at most 100 characters), or ''. */
  readonly reason: string;
  /** Who flagged it (Teslyn Cloud stamps the staff register's name). */
  readonly by_name: string;
}

/** What a new flag is, before it is saved. */
export interface ReflyFlagDraft {
  readonly line_status_id: Uuid;
  readonly zfid_start: number;
  readonly zfid_end: number;
  readonly from_m: number;
  readonly to_m: number;
  readonly test_key: string;
  readonly reason: string;
}

const cm = (m: number): number => Math.round(m * 100) / 100;

function originalOf(status: LineStatus, plan: readonly PlannedLine[]): PlannedLine | undefined {
  const parsed = parseLineNumber(status.line_number);
  if (!parsed.production) return undefined;
  return plan.find(
    (p) => p.deleted_at === null && p.number === parsed.base && plannedLineRole(p) === 'original',
  );
}

/** The newest live Flight data QC result of a line status that has zFids. */
export function newestQcResult(
  status: Pick<LineStatus, 'id'>,
  qc: readonly QcLineResult[],
): QcLineResult | null {
  return (
    [...qc]
      .filter((r) => r.deleted_at === null && r.line_status_id === status.id && r.fid_start !== null)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))[0] ?? null
  );
}

/**
 * The section two zFids mark on a flown line (R127), in either order: placed along the original line through the
 * newest Flight data QC result of the line status. A reason in words when it cannot be placed.
 */
export function reflyFlagSection(input: {
  readonly status: LineStatus;
  readonly plan: readonly PlannedLine[];
  readonly qc: readonly QcLineResult[];
  readonly zfids: readonly [number, number];
  readonly test_key?: string;
  readonly reason?: string;
}): ReflyFlagDraft | { readonly error: string } {
  const { status } = input;
  const original = originalOf(status, input.plan);
  if (!original) return { error: `Line ${status.line_number} has no original line in the plan to flag.` };
  const result = newestQcResult(status, input.qc);
  const run = result ? qcFidRun(result, status, original) : null;
  if (!result || !run)
    return {
      error: `Line ${status.line_number} has no Flight data QC result to place a zFid on: run its flight's QC first.`,
    };
  const [a, b] = input.zfids;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return { error: 'Enter both zFids of the section.' };
  const lo = Math.round(Math.min(a, b));
  const hi = Math.round(Math.max(a, b));
  if (lo < run.fid_start || hi > run.fid_end)
    return {
      error: `The section must lie in line ${status.line_number}'s flown run, ${zfidRangeText(run.fid_start, run.fid_end)}.`,
    };
  const p = alongOfFid(run, lo);
  const q = alongOfFid(run, hi);
  if (p === null || q === null)
    return { error: `The zFids are not in line ${status.line_number}'s flown run.` };
  const from_m = cm(Math.max(0, Math.min(p, q)));
  const to_m = cm(Math.min(original.length_m, Math.max(p, q)));
  if (to_m - from_m < 1)
    return { error: 'The section must cover at least 1 m of the line: choose two zFids further apart.' };
  // the zFid at each end along the line: a line flown against its planned direction has the larger zFid first
  const forward = p <= q;
  return {
    line_status_id: status.id,
    zfid_start: forward ? lo : hi,
    zfid_end: forward ? hi : lo,
    from_m,
    to_m,
    test_key: input.test_key ?? '',
    reason: (input.reason ?? '').trim().slice(0, FLAG_REASON_MAX),
  };
}

/** `Flagged for refly: zFid 4545650 to 4564900`, the review's reason when the processor typed none. */
export function flagReviewReason(
  flags: ReadonlyArray<Pick<ReflyFlagDraft, 'zfid_start' | 'zfid_end'>>,
  typed = '',
): string {
  const t = typed.trim();
  if (t) return t.slice(0, FLAG_REASON_MAX);
  const sorted = [...flags].map(zfidSpan).sort((x, y) => x[0] - y[0]);
  const text =
    sorted.length === 1
      ? `Flagged for refly: ${zfidRangeText(sorted[0]![0], sorted[0]![1])}`
      : `Flagged for refly: ${sorted.map(([a, b]) => `${formatZfid(a)}-${formatZfid(b)}`).join(', ')}`;
  return text.length <= FLAG_REASON_MAX ? text : `${text.slice(0, FLAG_REASON_MAX - 1)}…`;
}

/** A flag's zFids, lower first ("zFid 4545650 to 4564900"). */
export function zfidSpan(f: Pick<ReflyFlagDraft, 'zfid_start' | 'zfid_end'>): [number, number] {
  return [Math.min(f.zfid_start, f.zfid_end), Math.max(f.zfid_start, f.zfid_end)];
}

/** A flag in words: `zFid 4545650 to 4564900`. */
export function flagZfidText(f: Pick<ReflyFlagDraft, 'zfid_start' | 'zfid_end'>): string {
  const [a, b] = zfidSpan(f);
  return zfidRangeText(a, b);
}

/** A flag as a bad section (source `processor`), with its exact zFids. */
export function flagBadSection(f: ReflyFlagDraft & Partial<Pick<ReflyFlag, 'by_name'>>): BadSection {
  const who = f.by_name ? ` by ${f.by_name}` : '';
  return {
    source: 'processor',
    from_m: f.from_m,
    to_m: f.to_m,
    label: `Flagged for refly${who}: ${flagZfidText(f)}${f.reason ? ` (${f.reason})` : ''}`,
    zfids: { from: f.zfid_start, to: f.zfid_end },
    ...(f.test_key ? { test_key: f.test_key } : {}),
    ...(f.reason ? { reason_text: f.reason } : {}),
  };
}

/** The live flags of each line status, as bad sections, in order along the line. */
export function flagBadSections(flags: readonly ReflyFlag[]): Map<Uuid, BadSection[]> {
  const out = new Map<Uuid, BadSection[]>();
  for (const f of liveFlags(flags))
    out.set(f.line_status_id, [...(out.get(f.line_status_id) ?? []), flagBadSection(f)]);
  return out;
}

/** The live flags, oldest first. */
export function liveFlags(flags: readonly ReflyFlag[], lineStatusId?: Uuid): ReflyFlag[] {
  return flags
    .filter((f) => f.deleted_at === null && (lineStatusId === undefined || f.line_status_id === lineStatusId))
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));
}

/**
 * MB's patch over a line's flags (decision 155: one patch spanning every flagged section; 194 for a tie line), or a
 * full reflight when the patch would cover more than half of a line under 20 km. Null when the line has none.
 */
export function flagSuggestion(
  status: LineStatus,
  flags: ReadonlyArray<ReflyFlagDraft & Partial<Pick<ReflyFlag, 'by_name'>>>,
  plan: readonly PlannedLine[],
): PatchSuggestion | null {
  const s = suggestPatch(status, flags.map(flagBadSection), plan);
  if (!s) return null;
  return { ...s, reason: flagReviewReason(flags, flags.find((f) => f.reason)?.reason ?? '') };
}

/** One QC exceedance of a line to pick a flag from: its test and its zFids. */
export interface FlagEvent {
  readonly test_key: string;
  readonly label: string;
  readonly status: 'fail' | 'warn';
  readonly event: QcEvent;
}

/** The exceedances of the line status's newest Flight data QC result, fails first, then checks, in zFid order. */
export function flagEvents(status: Pick<LineStatus, 'id'>, qc: readonly QcLineResult[]): FlagEvent[] {
  const result = newestQcResult(status, qc);
  if (!result) return [];
  const out: FlagEvent[] = [];
  for (const t of result.tests) {
    if (t.status !== 'fail' && t.status !== 'warn') continue;
    for (const e of t.events) out.push({ test_key: t.key, label: t.label, status: t.status, event: e });
  }
  return out.sort(
    (a, b) =>
      (a.status === 'fail' ? 0 : 1) - (b.status === 'fail' ? 0 : 1) || a.event.fid_start - b.event.fid_start,
  );
}
